import { useEffect, useState } from 'react';
import { DropdownMenuItem } from '@components';
import { t } from 'i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReportFoldersFilters } from '@views/pages/Reporting/ReportFolders/types.ts';
import { useReportStore } from '@store';
import { omitBy, isNil, isEqual } from 'lodash';
import { useReportFoldersQuery } from 'common/cache';

const emptyFilters: ReportFoldersFilters = {
  search: '',
  page: 1,
  limit: 100,
  sort_by: 'name',
  sort_order: 'ASC',
};

export const useReportFolders = () => {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsObj = Object.fromEntries(searchParams.entries());

  const initialFilters = omitBy({ ...emptyFilters, ...searchParamsObj }, isNil);

  const [tabIndex, setTabIndex] = useState('1');
  const [filters, setFilters] = useState<ReportFoldersFilters>(initialFilters);
  const [isAddReportFolderModalOpen, setIsAddReportFolderModalOpen] =
    useState(false);
  const navigate = useNavigate();
  const { clearAll } = useReportStore();

  const {
    data: allCount = {
      total: 0,
    },
  } = useReportFoldersQuery({
    ...emptyFilters,
  });

  const {
    data: createdBy = {
      total: 0,
    },
  } = useReportFoldersQuery({
    ...emptyFilters,
    created_by_me: true,
  });

  const {
    data: favoriteCount = {
      total: 0,
    },
  } = useReportFoldersQuery({
    ...emptyFilters,
    favorites_only: true,
  });

  const {
    data: recentCount = {
      total: 0,
    },
  } = useReportFoldersQuery({
    ...emptyFilters,
    recently_viewed: true,
  });

  const isReportingSelected = pathname === '/reporting/reports';
  const isInitialFilters = isEqual(
    { ...filters, page: 1, limit: 100 },
    { ...emptyFilters, page: 1, limit: 100 },
  );

  useEffect(() => {
    setSearchParams(
      prev => {
        return { ...prev, ...omitBy(filters, isNil) };
      },
      { replace: true },
    );
  }, [filters]);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setTabIndex(newValue);
    setFilters({ ...filters, page: 1 });
  };

  const handleFilterChange = (newFilters: Partial<ReportFoldersFilters>) => {
    setFilters({ ...filters, page: 1, ...newFilters });
  };

  const handleCloseAddReportFolderModal = () => {
    setIsAddReportFolderModalOpen(false);
  };

  const dropdownMenu: DropdownMenuItem[] = [
    {
      iconName: 'report',
      label: t('reporting.dropdown.report'),
      onClick: () => {
        clearAll();
        navigate('/reporting/create');
      },
    },
    {
      iconName: 'folder',
      label: t('reporting.dropdown.folder'),
      onClick: () => setIsAddReportFolderModalOpen(true),
    },
  ];

  return {
    tabIndex,
    handleChangeTab,
    dropdownMenu,
    filters,
    handleFilterChange,
    isReportingSelected,
    isAddReportFolderModalOpen,
    handleCloseAddReportFolderModal,
    isInitialFilters,
    allCount: allCount.total,
    createdByCount: createdBy.total,
    favoriteCount: favoriteCount.total,
    recentCount: recentCount.total,
  };
};
